/** /grid_presets/ — saved data-grid views (presets).
 *
 * Hand-authored wrapper (console convention: never depend on generated types
 * for feature modules). The `settings` blob is client-owned; schema v1 below.
 * `pivot` is RESERVED — DataGrid Pro has no pivoting; the key exists so the
 * backend contract never changes when it arrives.
 */
import { z } from 'zod';

import { apiDelete, apiGet, apiPatch, apiPost } from './client';
import type { PaginatedResponse } from './users';

export const GRID_CODENAMES = [
  'bookings',
  'orders',
  'users_system',
  'users_staff',
  'users_guest',
  'audit',
  'bgtasks',
  'sessions',
  'access_groups',
  'templates',
  'template_groups',
  'tour_operators',
  'tours',
  'regions',
  'trip_types',
  'launch_policies',
  'pre_trip_items',
  'pre_trip_item_types',
  'activities',
  'activity_categories',
  'route_stops',
  'route_sets',
  'vehicles',
  'itineraries',
  'otas',
  'tag_schemas',
  'tag_categories',
  'tags',
  'delivery_logs',
  'materials',
  'import_runs',
  'api_requests',
  'notification_subscriptions',
  'api_credentials',
  'external_access',
  'onboarding_invites',
  'tenant_requests',
  'ext_ids',
  'notifications',
  'notification_feature_flags',
  'content_search',
  'changes',
  'ai_spend',
  'ai_changes',
  'support_tickets',
  'platform_notices',
  'mcp_limit_hits',
  'knowledge_gaps',
  'mcp_diagnose_runs',
  // Yes, the preset-management grid has presets of its own — every grid does.
  'grid_presets',
] as const;
export type GridCodename = (typeof GRID_CODENAMES)[number];

export interface PresetOwner {
  id: number;
  email: string;
  first_name: string;
  last_name: string;
}

/** Enum (not a bool) so future scopes — e.g. team-level presets — are
 *  additive values, not a contract change. */
export const PRESET_VISIBILITIES = ['private', 'shared'] as const;
export type PresetVisibility = (typeof PRESET_VISIBILITIES)[number];

export interface GridPresetListItem {
  id: number;
  project: 'console';
  grid: string;
  name: string;
  visibility: PresetVisibility;
  owner: PresetOwner;
  created_ts: string;
  updated_ts: string;
}

export interface GridPresetDetail extends GridPresetListItem {
  settings: PresetSettingsV1;
}

export interface GridPresetChange {
  id: number;
  action: 'created' | 'updated' | 'deleted' | 'restored';
  actor: PresetOwner | null;
  created_ts: string;
  state_before: Record<string, unknown> | null;
  state_after: Record<string, unknown> | null;
  restored_from_change_id: number | null;
}

/** Settings schema v1. Every section is optional — the save dialog lets the
 *  user pick which sections a preset covers. `filters` is grid-family
 *  specific: bookings stores `{q: "<DSL text>"}`, resource grids store
 *  `{params: {<url param>: <value>}}`. */
export interface PresetSettingsV1 {
  version: 1;
  columns?: { order: string[]; visibility: Record<string, boolean> };
  filters?: unknown;
  ordering?: string;
  pivot?: null;
}

export const presetSettingsSchema = z
  .object({
    version: z.literal(1),
    columns: z
      .object({ order: z.array(z.string()), visibility: z.record(z.boolean()) })
      .optional(),
    filters: z.unknown().optional(),
    ordering: z.string().optional(),
    pivot: z.null().optional(),
  })
  .passthrough();

/** Tolerant read: a corrupt/foreign-versioned blob degrades to an empty
 *  preset instead of crashing the grid. */
export function parseSettings(raw: unknown): PresetSettingsV1 {
  const parsed = presetSettingsSchema.safeParse(raw);
  if (!parsed.success) {
    console.warn('grid preset: unrecognized settings blob, ignoring', parsed.error.issues);
    return { version: 1 };
  }
  return parsed.data as PresetSettingsV1;
}

export interface ListParams {
  grid?: GridCodename | string;
  search?: string;
  ordering?: string;
  limit?: number;
  offset?: number;
  all?: boolean;
}

export async function list(params: ListParams = {}): Promise<PaginatedResponse<GridPresetListItem>> {
  const query: Record<string, string | number | boolean> = {};
  if (params.grid) query.grid = params.grid;
  if (params.search) query.search = params.search;
  if (params.ordering) query.ordering = params.ordering;
  if (params.limit !== undefined) query.limit = params.limit;
  if (params.offset !== undefined) query.offset = params.offset;
  if (params.all) query.all = 'true';
  return apiGet<PaginatedResponse<GridPresetListItem>>('/grid_presets/', { params: query });
}

export async function retrieve(id: number): Promise<GridPresetDetail> {
  return apiGet<GridPresetDetail>(`/grid_presets/${id}/`);
}

export async function create(body: {
  grid: GridCodename | string;
  name: string;
  visibility: PresetVisibility;
  settings: PresetSettingsV1;
}): Promise<GridPresetDetail> {
  return apiPost<GridPresetDetail>('/grid_presets/', body);
}

export async function update(
  id: number,
  patch: { name?: string; visibility?: PresetVisibility; settings?: PresetSettingsV1 },
): Promise<GridPresetDetail> {
  return apiPatch<GridPresetDetail>(`/grid_presets/${id}/`, patch);
}

export async function remove(id: number): Promise<void> {
  await apiDelete<void>(`/grid_presets/${id}/`);
}

export async function history(
  id: number,
  params: { limit?: number; offset?: number } = {},
): Promise<PaginatedResponse<GridPresetChange>> {
  return apiGet<PaginatedResponse<GridPresetChange>>(`/grid_presets/${id}/history/`, { params });
}

export async function restore(id: number, changeId: number): Promise<GridPresetDetail> {
  return apiPost<GridPresetDetail>(`/grid_presets/${id}/restore/`, { change_id: changeId });
}

/** Duplicate-name 400s come back as {"name": ["…"]}; surface the field text. */
export function presetErrorMessage(err: unknown): string {
  const body = (err as { body?: { name?: string[]; message?: string } })?.body;
  return body?.name?.[0] ?? body?.message ?? (err as Error)?.message ?? 'Request failed';
}
